import { KeyValue } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { ApplicationService } from '@app/store/application/application.service';
import { BrandConfigQuery } from '@app/store/brand/brand.query';
import { BreedsQuery } from '@app/store/breeds/breeds.query';
import { QuoteStore } from '@app/store/quote/quote.store';
import { QuoteSessionQuery } from '@app/store/session/session.query';
import { QuoteSessionService } from '@app/store/session/session.service';
import { formatPhoneNumber, stripSpecialChars } from '@app/utils/phone-number-formatter';
import { Ages } from '@constants/ages';
import {
	EVENTS,
	FORM_FIELD_TYPES,
	FORM_NAMES,
	REDACTED,
	CLICK_LOCATIONS,
	CLICK_TYPES,
	ERROR_TYPES
} from '@constants/analytics';
import { getCdnAsset } from '@constants/api-endpoints';
import { API_ERRORS } from '@constants/api-errors';
import { DATADOG_EVENTS, DATADOG_PAGES } from '@constants/datadog';
import { GENERIC_PARTNER_ORG_CODE } from '@constants/generic-partner';
import { NAME_MAX_LENGTH } from '@constants/name-length';
import { REFERRAL_LIST } from '@constants/referral-list';
import { ROUTES } from '@constants/routes';
import STATES from '@constants/state-list.json';
import { sexOptions,speciesOptions, serviceDogOptions, additionalPetsOptions } from '@constants/toggle-options';
import { petNameRegEx } from '@constants/validation-regex';
import { YES_NO } from '@constants/YesNo';
import { datadogRum } from '@datadog/browser-rum';
import { Breed } from '@models/Breed';
import { Contact } from '@models/Contact';
import { QuoteSession } from '@models/QuoteSession';
import { AnalyticsService } from '@services/analytics.service';
import { ExperimentationService } from '@services/experimentation.service';
import { OrchestratorService } from '@services/orchestrator/orchestrator.service';
import { PlacesService } from '@services/places.service';
import { RetrieveCookieService } from '@services/retrieve-cookie.service';
import { UtmService } from '@services/utm.service';
import { isModernLink } from '@utils/brand.util';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Observable, Subject, firstValueFrom } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

import { environment } from '../../../environments/environment';

import { AdobeTagService } from './../../services/adobeTag.service';

@Component({
	standalone: false,
	selector: 'qe-pet-information',
	templateUrl: './pet-information.component.html',
	styleUrls: ['./pet-information.component.scss']
})
export class PetInformationComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild(`employerNameEl`)
	private readonly employerNameEl?: ElementRef<HTMLInputElement>;
	public readonly brandPhoneNumber$: Observable<string | undefined>;
	public readonly experimentationService: ExperimentationService;
	public ageListMock: Map<number, string> = new Ages().values;
	public heardAboutMock: Array<KeyValue<number, string>> = REFERRAL_LIST;
	public catBreeds: Breed[] = [];
	public dogBreeds: Breed[] = [];
	public breedsList: Breed[] = [];
	public petInformationForm!: FormGroup;
	public employerForm!: FormGroup;
	public showAgentForm: boolean = false;
	public agentForm!: FormGroup;
	public formInvalid: boolean = false;
	public policyHolderExists: boolean = false;
	public ruleError: boolean = false;
	public ruleErrorList: string[] = [];
	public quoteRetrievalError: boolean = false;
	public focusedInput: string | undefined = undefined;
	public serviceDogEnabled: boolean = false;
	public showServiceDogOption: boolean = false;
	public pets: FormGroup[] = [];
	public petNameParam: string = '';
	public readonly stateList: Array<{ name: string; abbreviation: string }> = STATES;
	/** Will hide/show employer form for Generic Org code entries */
	public showEmployerForm: boolean = false;
	public readonly afterYouEnroll: string[] = [
		`Embrace will request your pet's medical records from your Vet`,
		`After your waiting period, your pet is covered`,
		`Rest assured coverage will be there when you need it`
	];
	public submittingForm = false;
	public smsTermsAndConditionsUrl: string;
	public privacyPolicyUrl: string;
	public myEmbraceUrlChangeCoverage: string;
	public myEmbraceUrlAddAPet: string;
	public getCdnAsset = getCdnAsset;
	public get submitButtonLabel(): string {
		return 'Select Coverage';
	}
	public sexOptions = sexOptions;
	public speciesOptions = speciesOptions;
	public additionalPetsOptions = additionalPetsOptions;
	public serviceDogOptions = serviceDogOptions;
	private readonly ngUnsubscribe$: Subject<boolean> = new Subject();
	private readonly zipLength: number = 5;
	private readonly pageTitle = 'Pet Information | Embrace QuoteEngine';
	private params!: ParamMap;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly adobeTagService: AdobeTagService,
		private readonly router: Router,
		private readonly fb: FormBuilder,
		private readonly titleService: Title,
		private readonly storeActions: QuoteSessionService,
		private readonly appActions: ApplicationService,
		private readonly sessionQuery: QuoteSessionQuery,
		private readonly breedsQuery: BreedsQuery,
		private readonly orchestratorService: OrchestratorService,
		private readonly gtmService: AnalyticsService,
		private readonly utmService: UtmService,
		private readonly recaptchaV3Service: ReCaptchaV3Service,
		private readonly brandConfigQuery: BrandConfigQuery,
		private readonly placesService: PlacesService,
		private readonly quoteStore: QuoteStore,
		private readonly retrieveCookieService: RetrieveCookieService,
		experimentationService: ExperimentationService
	) {
		this.experimentationService = experimentationService;
		this.smsTermsAndConditionsUrl = environment.smsTermsAndConditionsUrl;
		this.privacyPolicyUrl = environment.privacyPolicyUrl;
		this.myEmbraceUrlChangeCoverage = `${environment.myEmbraceUrl}#/ChangeCoverage`;
		this.myEmbraceUrlAddAPet = `${environment.myEmbraceUrl}#/ChangeCoverage?referral=addpet`;
		this.brandPhoneNumber$ = this.brandConfigQuery.phone$().pipe(map(phone => formatPhoneNumber(phone)));
		this.quoteRetrievalError = this.router.getCurrentNavigation()?.extras.state?.quoteRetrievalError;

		this.route.queryParamMap.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(params => {
			if (params.keys.length) {
				this.params = params;
			}

			if (params.keys.includes('petName')) {
				this.focusedInput = 'petName';
				this.petNameParam = params.get('petName') || '';
			}

			if (params.keys.includes('zip') || params.keys.includes('zipCode') || params.keys.includes('zip_code')) {
				this.focusedInput = 'zipCode';
			}
		});

		this.breedsQuery.selectAll().pipe(takeUntil(this.ngUnsubscribe$)).subscribe(allBreeds => {
			this.dogBreeds = allBreeds.filter(({ speciesName }) =>

				speciesName.toLowerCase() === 'dog'
			);

			this.catBreeds = allBreeds.filter(({ speciesName }) =>

				speciesName.toLowerCase() === 'cat'
			);

			this.breedsList = this.dogBreeds;
		});

		this.petInformationForm = this.fb.group(
			{
				petName: [
					this.petNameParam ? this.petNameParam : '',
					[Validators.required, Validators.pattern(petNameRegEx), Validators.maxLength(NAME_MAX_LENGTH)]
				],
				species: ['', { validators: Validators.required, updateOn: `change` }],
				serviceDog: ['', { updateOn: `change` }],
				sex: ['', { validators: Validators.required, updateOn: `change` }],
				breed: ['', { validators: Validators.required, updateOn: `blur` }],
				age: ['', { validators: Validators.required, updateOn: `change` }],
				zipCode: [
					this.zipCodeParamValue,
					{
						validators: [
							Validators.required,
							Validators.minLength(this.zipLength),
							Validators.maxLength(this.zipLength),
							Validators.pattern(/^\d*$/im)
						],
						updateOn: `blur`
					}
				],
				firstName: [{ value: '', disabled: true }],
				lastName: [{ value: '', disabled: true }],
				emailAddress: ['', [Validators.required, Validators.email]],
				phoneNumber: [
					'',
					[
						/* eslint-disable @typescript-eslint/no-magic-numbers */
						Validators.minLength(10),
						Validators.maxLength(16)
					]
				],
				optInTextMessage: [false],
				heardAbout: [null],
				additionalPets: ['', Validators.required],
			},
			{ updateOn: `blur` }
		);

		this.petInformationForm.valueChanges.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(ev => {
			if (this.formInvalid) {
				this.formInvalid = false;
				this.petInformationForm.updateValueAndValidity();
			}
		});

		this.species?.valueChanges
			.pipe(
				takeUntil(this.ngUnsubscribe$),
			)
			.subscribe(
				species => {
					if (this.breed?.value) {
						this.breed?.patchValue(``);
					}
					this.breedsList = this.species?.value === 'dog' ? this.dogBreeds : this.catBreeds;
					this.showServiceDogOption = this.serviceDogEnabled && species === 'dog';

					// when species changes, we need to set conditional validation and reset servicedog
					if (this.showServiceDogOption) {
						this.serviceDog?.setValidators(Validators.required);
						this.serviceDog?.updateValueAndValidity();
					} else {
						this.serviceDog?.removeValidators(Validators.required);
						this.serviceDog?.updateValueAndValidity();
					}

					this.serviceDog?.reset('');
				},
				err => {
					this.router.navigate([ROUTES.ERROR]);
					console.error(err);
				}
			);

		this.brandConfigQuery
			.featureFlags$()
			.pipe(
				takeUntil(this.ngUnsubscribe$)
			).subscribe(flags => {
				this.serviceDogEnabled = flags?.find((flag) => flag?.feature === 'service_dog')?.enabled || false;

				if (this.serviceDogEnabled) {
					this.serviceDog?.setValidators(Validators.required);
					this.serviceDog?.updateValueAndValidity();
				}
			});

		this.brandConfigQuery
			.showAgentForm$()
			.pipe(
				takeUntil(this.ngUnsubscribe$)
			).subscribe(showAgentForm => {
				this.showAgentForm = showAgentForm;
				if (this.showAgentForm) {
					this.initAgentForm();
				}
			});

		this.sessionQuery
			.contactState$()
			.pipe(
				takeUntil(this.ngUnsubscribe$),
				filter(contact => !!contact)
			)
			.subscribe(contact => {
				if (contact.emailAddress) {
					this.emailAddress?.patchValue(contact.emailAddress);
				}
				if (contact.phoneNumber) {
					this.phoneNumber?.patchValue(contact.phoneNumber);
				}
				if (contact.ratingZipCode) {
					this.zipCode?.patchValue(contact.ratingZipCode);
				}
				if (contact.firstName) {
					this.firstName?.patchValue(contact.firstName);
				}
				if (contact.lastName) {
					this.lastName?.patchValue(contact.lastName);
				}
			});
	}
	public get petName(): AbstractControl | null {
		return this.petInformationForm.get(`petName`);
	}
	public get species(): AbstractControl | null {
		return this.petInformationForm.get(`species`);
	}
	public get serviceDog(): AbstractControl | null {
		return this.petInformationForm.get(`serviceDog`);
	}
	public get sex(): AbstractControl | null {
		return this.petInformationForm.get(`sex`);
	}
	public get breed(): AbstractControl | null {
		return this.petInformationForm.get(`breed`);
	}
	public get age(): AbstractControl | null {
		return this.petInformationForm.get(`age`);
	}
	public get zipCode(): AbstractControl | null {
		return this.petInformationForm.get(`zipCode`);
	}
	public get firstName(): AbstractControl | null {
		return this.petInformationForm.get(`firstName`);
	}
	public get lastName(): AbstractControl | null {
		return this.petInformationForm.get(`lastName`);
	}
	public get emailAddress(): AbstractControl | null {
		return this.petInformationForm.get(`emailAddress`);
	}
	public get phoneNumber(): AbstractControl | null {
		return this.petInformationForm.get(`phoneNumber`);
	}
	public get optInTextMessage(): AbstractControl | null {
		return this.petInformationForm.get(`optInTextMessage`);
	}
	public get heardAbout(): AbstractControl | null {
		return this.petInformationForm.get(`heardAbout`);
	}
	public get addtionalPetsControl(): AbstractControl | null {
		return this.petInformationForm.get(`additionalPets`);
	}
	public get additionalPets(): boolean | null {
		const value = this.addtionalPetsControl?.value;

		return YES_NO.get(value) ?? value;
	}
	public get policyHasPets(): boolean {
		return this.sessionQuery.getValue().pets?.length > 0;
	}
	public get partnerSenderId(): AbstractControl | null {
		return this.agentForm.get(`partnerSenderId`);
	}
	// Employer Info
	public get employerName(): AbstractControl | null {
		return this.employerForm.get(`name`);
	}
	public get employerAddress(): AbstractControl | null {
		return this.employerForm.get(`address`);
	}
	public get employerCity(): AbstractControl | null {
		return this.employerForm.get(`city`);
	}
	public get employerState(): AbstractControl | null {
		return this.employerForm.get(`state`);
	}
	public get employerZipCode(): AbstractControl | null {
		return this.employerForm.get(`zipCode`);
	}
	public get showAllstateOptOut(): boolean {
		return this.brandParamValue.toLowerCase() === 'allstate';
	}
	public get formattedBrand(): string {
		return this.brandParamValue.slice(0, 1).toUpperCase() + this.brandParamValue.slice(1);
	}
	public get petControl(): AbstractControl | null {
		return this.petInformationForm?.get('pet');
	}
	private get zipCodeParamValue(): string {
		return (this.params?.get('zipCode') || this.params?.get('zip') || this.params?.get('zip_code')) ?? '';
	}
	private get brandParamValue(): string {
		return this.params?.get('brand') || '';
	}
	async ngOnInit(): Promise<void> {
		this.titleService.setTitle(this.pageTitle);
		const orgCode = await firstValueFrom(this.sessionQuery.organizationCode$());
		const brand = await firstValueFrom(this.sessionQuery.brand$());
		if (brand.toLowerCase() === 'allstate') {
			this.adobeTagService.loadAllstateScript(environment.allstateTagManagerURL);
		}

		if (this.titleService.getTitle() === typeof String) {
			throw new Error('This is a test error');
		}

		this.showEmployerForm = orgCode === GENERIC_PARTNER_ORG_CODE;

		if (this.showEmployerForm) this.initEmployerForm();

		const utmParams = this.utmService.getUtmCookie();

		// This event triggers important lead tracking tags in GTM like Impact. Don't touch.
		this.gtmService.push({
			event: 'checkout',
			pagePath: 'pet-information',
			// eslint-disable-next-line max-len
			originalLocation: `${document.location.protocol}//${document.location.hostname}${document.location.pathname}${document.location.search}`,
			utm_source: utmParams.source,
			utm_medium: utmParams.medium,
			utm_term: utmParams.term,
			utm_campaign: utmParams.campaign
		});

		// Google Analytics 4
		this.gtmService.push({
			event: 'quote_engine_checkout_ga4',
			checkoutStep: 1,
			funnelStepName: 'Pet Information',
			conversionName: 'Quote Start',
			brandName: brand.toLowerCase()
		});

		await this.experimentationService.updateExperimentUser();
		this.experimentationService.logEvent('page_1_load');

		datadogRum.addAction(DATADOG_EVENTS.PAGE_VIEW, {
			page: DATADOG_PAGES.PET_INFO,
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => {
			if (this.employerNameEl) {
				this.placesService
					.initAutocomplete(this.employerNameEl.nativeElement, {
						fields: [`address_components`, `name`],
						types: [`establishment`]
					})
					.pipe(takeUntil(this.ngUnsubscribe$))
					.subscribe(({ businessName, address }) => {
						this.employerForm.patchValue({
							name: businessName,
							address: address.addressLine1,
							city: address.city,
							state: address.state,
							zipCode: address.zipCode
						});
					});
			}
			this.gtmService.push({
				event: 'Loaded Pet Information Page (New Quote)'
			});
		});
	}

	public checkPhoneNumber(): void {
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: 'phoneNumber',
			field_value: REDACTED,
			field_type: FORM_FIELD_TYPES.INPUT,
			field_id: `${FORM_FIELD_TYPES.INPUT}-${FORM_NAMES.PET_INFO}-phoneNumber`
		});
		this.focusedInput = undefined;
	}

	public handleOptInTextMessageCheckbox(el: HTMLEpiCheckboxElement): void {
		this.optInTextMessage?.setValue(el.checked);
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: 'optInTextMessage',
			field_value: el.value,
			field_type: FORM_FIELD_TYPES.CHECKBOX,
			field_id: `${FORM_FIELD_TYPES.CHECKBOX}-${FORM_NAMES.PET_INFO}-optInTextMessage`
		});
	}

	public petNameFocused(): boolean {
		return this.focusedInput === `petName`;
	}

	public handleEpiSelectionChange(el: HTMLEpiSelectElement): void { }

	// eslint-disable-next-line complexity
	public async submitForm(): Promise<void> {
		this.submittingForm = true;
		const recaptchaToken = await firstValueFrom(this.recaptchaV3Service.execute('start_quote'));
		this.quoteRetrievalError && (this.quoteRetrievalError = false);
		this.policyHolderExists && (this.policyHolderExists = false);
		this.ruleError && (this.ruleError = false);
		this.ruleErrorList.length = 0;
		this.petInformationForm.markAllAsTouched();

		if (
			this.petInformationForm.invalid ||
			(this.showEmployerForm && this.employerForm.invalid) ||
			(this.showAgentForm && this.agentForm.invalid)
		) {
			this.formInvalid = true;
			this.submittingForm = false;

			return;
		}

		const contact = this.sessionQuery.getContact();
		const {
			zipCode: ratingZipCode,
			firstName,
			lastName,
			emailAddress,
			phoneNumber,
			heardAbout,
			petName,
			species,
			serviceDog,
			age,
			sex,
			breed,
			additionalPets
		} = this.petInformationForm.getRawValue();

		// transform pet for request
		const transformedPet = {
			name: petName,
			species,
			breedId: breed,
			gender: sex,
			breedName: this.getBreedNameById(breed),
			isServiceDog: serviceDog === 'yes' ? true : false,
			ageInYears: age
		};

		// show loader
		this.appActions.setLoading(true);

		const strippedPhoneNumber = stripSpecialChars(phoneNumber);
		const cleanedPhoneNumber = strippedPhoneNumber.startsWith('1') ? strippedPhoneNumber.slice(1) : strippedPhoneNumber;
		const analyticsInfo = this.utmService.getUtmCookie();
		const session = await firstValueFrom(this.sessionQuery.session$());

		// if phone number is valid, the opt in value should be true, else false
		const optInTextMessageAndValidPhoneNumber = cleanedPhoneNumber.length === 10;

		let generateQuoteRequest: Partial<QuoteSession> = {
			contact: {
				ratingZipCode,
				emailAddress,
				phoneNumber: cleanedPhoneNumber,
				organizationCode: contact.organizationCode,
				organizationMemberIdentifier: contact.organizationMemberIdentifier,
				optInTextMessage: optInTextMessageAndValidPhoneNumber,
				heardAboutUs: heardAbout ?? 0,
				firstName: firstName || contact?.firstName || ``,
				lastName: lastName || contact?.lastName || ``,
				partnerSenderId: contact.partnerSenderId ?? ``,
				brand: contact.brand ?? ``,
				mailingAddress: contact.mailingAddress,
				billingInformation: contact.billingInformation,
				hasMoreThanOnePet: additionalPets === 'yes' ? true : false,
			},
			pets: [
				transformedPet
			],
			analyticsInfo,
			delayPolicyStartDateDays: session.delayPolicyStartDateDays,
			subProducerNumber: session.subProducerNumber,
			verificationInfo: {
				expectedAction: 'start_quote',
				captchaToken: recaptchaToken
			},
		};
		this.quoteStore.update(generateQuoteRequest);

		/** If the user gets the employer form it should send that info in the contact */
		if (this.showEmployerForm) {
			const { name: employerName, address: addressLine1, city, state, zipCode } = this.employerForm.getRawValue();
			const employerInfo: Pick<Contact, 'employerAddress' | 'employerName'> = {
				employerAddress: {
					addressLine1,
					city,
					state,
					zipCode
				},
				employerName
			};
			generateQuoteRequest = {
				...generateQuoteRequest,
				contact: {
					...generateQuoteRequest.contact,
					...employerInfo
				}
			};
		}

		if (this.showAgentForm) {
			const { partnerSenderId } = this.agentForm.getRawValue();

			generateQuoteRequest = {
				...generateQuoteRequest,
				contact: {
					...generateQuoteRequest.contact,
					partnerSenderId
				}
			};
		}

		this.experimentationService.logEvent('page_1_submit');

		this.gtmService.push({
			event: EVENTS.CLICK,
			click_location: CLICK_LOCATIONS.CONTENT_BOTTOM,
			click_text: this.submitButtonLabel,
			click_type: CLICK_TYPES.CTA,
			click_id: `${CLICK_TYPES.CTA}-${CLICK_LOCATIONS.CONTENT_BOTTOM}-Select-Coverage`
		});

		this.orchestratorService
			.generateNewQuote(generateQuoteRequest)
			.pipe(takeUntil(this.ngUnsubscribe$))
			.subscribe(
				quoteSession => {
					if (quoteSession) {
						this.storeActions.update({
							...quoteSession,
							quoteDetails: {
								...quoteSession.quoteDetails
							},
							contact: {
								...quoteSession.contact,
								vetSearchZip: ratingZipCode
							}
						});

						this.gtmService.push({
							event: 'Clicked Start Quote'
						});

						this.gtmService.push({
							event: 'start_a_quote_ga4',
							initialPetSpecies: species,
							initialPetSex: sex,
							initialPetBreed: transformedPet.breedName.toLowerCase(),
							initialPetAge: transformedPet.ageInYears
						});

						if (quoteSession.contact.id) {
							this.retrieveCookieService.update(quoteSession.contact.id);
						}

						datadogRum.addAction(DATADOG_EVENTS.PAGE_SUBMIT, {
							page: DATADOG_PAGES.PET_INFO,
							numberOfPets: 1,
							quoteId: quoteSession.id
						});

						this.experimentationService.logEvent('page_1_additional_pets_toggle', 1, {
							additionalPets: this.addtionalPetsControl?.value,
							initialPetSpecies: species,
							initialPetSex: sex,
							initialPetBreed: transformedPet.breedName.toLowerCase(),
							initialPetAge: age
						});

						// dirty
						this.appActions.setActivePet(quoteSession.pets[0].petId ?? ``);
						this.router.navigate([ROUTES.SELECT_A_POLICY]);
					}
				},
				(err: HttpErrorResponse) => {
					const { detail, title } = err.error;
					this.appActions.setLoading(false);
					this.submittingForm = false;
					switch (title) {
						case API_ERRORS.QUOTE_EXECUTION_ERROR:
						case API_ERRORS.CONTACT_VALIDATION_MESSAGE:
						case API_ERRORS.PAYROLLDEDUCT_VALIDATION_MESSAGE:
							this.ruleError = true;
							this.ruleErrorList = [
								...this.ruleErrorList,
								...(err.error.detail
									?.split(` :: `)[1]
									?.split(`;`)
									?.map((message: string) => message.trim()) ?? [])
							];

							return;
						case API_ERRORS.POLICY_HOLDER_EXISTS:
							this.policyHolderExists = true;

							return;
						default:
							break;
					}

					const errorDetail = detail
						?.split(` :: `)[1]
						?.split(`;`)
						?.map((message: string) => message.trim());

					this.gtmService.push({
						event: EVENTS.ERROR,
						error_type: ERROR_TYPES.GENERIC,
						error_text: errorDetail,
						error_id: `error_${ERROR_TYPES.GENERIC}-${title}`
					});

					this.router.navigate([ROUTES.ERROR], {
						state: {
							detail: errorDetail
						}
					});
					console.error(err);
				}
			);
	}

	public onFocus(el: string): void {
		this.focusedInput = el;
	}

	public onBlur(el: string): void {
		this.focusedInput = undefined;

		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: el,
			field_value: el === 'petName' ? this.petName?.value : REDACTED,
			field_type: FORM_FIELD_TYPES.INPUT,
			field_id: `${FORM_FIELD_TYPES.INPUT}-${FORM_NAMES.PET_INFO}-${el}`
		});
	}

	public onRadioChange(formField: string, value: string): void {
		this.gtmService.push({
			event: EVENTS.FORM_FIELD,
			form_name: FORM_NAMES.PET_INFO,
			field_name: formField,
			field_value: value,
			field_type: FORM_FIELD_TYPES.RADIO,
			field_id: `${FORM_FIELD_TYPES.RADIO}-${FORM_NAMES.PET_INFO}-${formField}`
		});
	}

	public isModernLinkBrand(): boolean {
		return isModernLink(this.brandParamValue);
	}

	private getBreedNameById(breedId: string): string {
		return this.breedsQuery.getAll().find(({ id }) => id === breedId)?.name ?? ``;
	}

	private initEmployerForm(): void {
		this.employerForm = this.fb.group(
			{
				name: ['', Validators.required],
				address: ['', Validators.required],
				city: ['', Validators.required],
				state: ['', Validators.required],
				zipCode: ['', Validators.required]
			},
			{ updateOn: `blur` }
		);
	}

	private initAgentForm(): void {
		this.agentForm = this.fb.group({
			partnerSenderId: ['', Validators.required]
		});
	}


	ngOnDestroy(): void {
		this.ngUnsubscribe$.next(true);
		this.ngUnsubscribe$.unsubscribe();
	}
}
